import moment from 'moment-timezone';
import { getUserTimezone } from './timezone';

/**
 * Converts a date to timezone-specific string format for timelog keys
 * @param {Date|string} date - Date to convert
 * @param {string} timeZone - Target timezone (required)
 * @returns {string} Formatted timezone string for timelog keys
 */
export const formatTimezoneString = (date, timeZone) => {
  try {
    if (!timeZone) {
      throw new Error('Timezone is required for formatTimezoneString');
    }

    const momentDate = moment(date).tz(timeZone);
    const formattedDate = momentDate.format('ddd MMM DD YYYY HH:mm:ss [GMT]ZZ');

    // Get timezone name using Intl API
    const timeZoneName =
      new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone,
        timeZoneName: 'long',
      })
        .formatToParts(new Date())
        .find((part) => part.type === 'timeZoneName')?.value || timeZone;

    return `${formattedDate} (${timeZoneName})`;
  } catch (error) {
    console.error('Error formatting timezone string:', error);
    // Fallback to ISO string if formatting fails
    return new Date(date).toISOString();
  }
};

/**
 * Generates date key in DD:MM:YYYY format for Firestore document paths
 * @param {Date|string} date - Date to convert
 * @param {string} timeZone - Target timezone (required)
 * @returns {string} Date key in DD:MM:YYYY format
 */
export const getDateKey = (date, timeZone) => {
  try {
    if (!timeZone) {
      throw new Error('Timezone is required for getDateKey');
    }

    const momentDate = moment(date).tz(timeZone);
    const dateKey = momentDate.format('D:M:YYYY');

    console.log('🟠 getDateKey: Generating date key', {
      inputDate: date,
      inputDateType: typeof date,
      timeZone,
      momentDate: momentDate.toString(),
      momentDateISO: momentDate.toDate().toISOString(),
      generatedDateKey: dateKey,
      momentFormatParts: {
        day: momentDate.format('D'),
        month: momentDate.format('M'),
        year: momentDate.format('YYYY'),
      },
    });

    return dateKey;
  } catch (error) {
    console.error('🟠 getDateKey: Error generating date key:', {
      error: error.message,
      inputDate: date,
      timeZone: timeZone,
    });

    // Fallback to local date formatting
    const fallbackDate = new Date(date);
    const fallbackKey = `${fallbackDate.getDate()}:${fallbackDate.getMonth() + 1}:${fallbackDate.getFullYear()}`;

    console.log('🟠 getDateKey: Using fallback date key', {
      fallbackDate: fallbackDate.toString(),
      fallbackKey,
    });

    return fallbackKey;
  }
};

/**
 * Checks if a timer session crosses midnight boundary
 * @param {Date|string} startTime - Session start time
 * @param {Date|string} endTime - Session end time
 * @param {string} timeZone - Session timezone (required)
 * @returns {boolean} True if session crosses midnight
 */
export const crossesMidnight = (startTime, endTime, timeZone) => {
  try {
    if (!timeZone) {
      throw new Error('Timezone is required for crossesMidnight');
    }

    const startMoment = moment(startTime).tz(timeZone);
    const endMoment = moment(endTime).tz(timeZone);

    // Get date strings in the session timezone
    const startDate = startMoment.format('YYYY-MM-DD');
    const endDate = endMoment.format('YYYY-MM-DD');

    console.log('🟡 crossesMidnight: Checking midnight boundary', {
      startTime: startTime,
      endTime: endTime,
      timeZone: timeZone,
      startMoment: startMoment.toString(),
      endMoment: endMoment.toString(),
      startDate,
      endDate,
      crossesMidnight: startDate !== endDate,
    });

    return startDate !== endDate;
  } catch (error) {
    console.error(
      '🟡 crossesMidnight: Error checking midnight crossing:',
      error
    );
    return false;
  }
};

/**
 * Gets all midnight boundaries between start and end time
 * @param {Date|string} startTime - Session start time
 * @param {Date|string} endTime - Session end time
 * @param {string} timeZone - Session timezone (required)
 * @returns {Array<Date>} Array of midnight timestamps
 */
export const getMidnightBoundaries = (startTime, endTime, timeZone) => {
  try {
    if (!timeZone) {
      throw new Error('Timezone is required for getMidnightBoundaries');
    }

    const startMoment = moment(startTime).tz(timeZone);
    const endMoment = moment(endTime).tz(timeZone);

    console.log('🟡 getMidnightBoundaries: Calculating boundaries', {
      startTime: startTime,
      endTime: endTime,
      timeZone: timeZone,
      startMoment: startMoment.toString(),
      endMoment: endMoment.toString(),
    });

    const boundaries = [];
    let current = startMoment.clone().add(1, 'day').startOf('day');

    console.log('🟡 getMidnightBoundaries: Starting boundary calculation', {
      firstBoundary: current.toString(),
    });

    while (current.isBefore(endMoment)) {
      console.log('🟡 getMidnightBoundaries: Adding boundary', {
        boundary: current.toString(),
        boundaryISO: current.toDate().toISOString(),
      });
      boundaries.push(current.toDate());
      current.add(1, 'day');
    }

    console.log('🟡 getMidnightBoundaries: Final boundaries', {
      count: boundaries.length,
      boundaries: boundaries.map((b) => b.toISOString()),
    });

    return boundaries;
  } catch (error) {
    console.error(
      '🟡 getMidnightBoundaries: Error getting midnight boundaries:',
      error
    );
    return [];
  }
};

/**
 * Splits a timer session into multiple timelog entries across date boundaries
 * @param {Date|string} startTime - Session start time
 * @param {Date|string} endTime - Session end time
 * @param {string} timeZone - Session timezone (required, from locked session)
 * @param {Object} sessionData - Session metadata (project, task, etc.)
 * @returns {Array<Object>} Array of timelog entries for each date
 */
export const splitSessionByDates = (
  startTime,
  endTime,
  timeZone,
  sessionData
) => {
  try {
    if (!timeZone) {
      throw new Error('Timezone is required for splitSessionByDates');
    }

    console.log('🟢 splitSessionByDates: Starting session split process', {
      startTime: startTime,
      endTime: endTime,
      timeZone: timeZone,
      sessionData: sessionData,
    });

    const startMoment = moment(startTime).tz(timeZone);
    const endMoment = moment(endTime).tz(timeZone);

    console.log('🟢 splitSessionByDates: Timezone conversion complete', {
      timeZone,
      startMoment: startMoment.toString(),
      endMoment: endMoment.toString(),
      startMomentISO: startMoment.toDate().toISOString(),
      endMomentISO: endMoment.toDate().toISOString(),
    });

    // If session doesn't cross midnight, return single entry
    const doesCrossMidnight = crossesMidnight(startTime, endTime, timeZone);
    console.log('🟢 splitSessionByDates: Midnight crossing check result', {
      doesCrossMidnight,
    });

    if (!doesCrossMidnight) {
      console.log('🟢 splitSessionByDates: Single day session detected');

      const totalSeconds = Math.floor(
        (endMoment.toDate() - startMoment.toDate()) / 1000
      );
      const taskHours = Math.floor(totalSeconds / 3600);
      const taskMinutes = Math.floor((totalSeconds % 3600) / 60);

      const singleEntry = [
        {
          date: getDateKey(endTime, timeZone),
          timestampKey: formatTimezoneString(endTime, timeZone),
          entry: {
            companyID: sessionData.companyId || '',
            idleMinutes: null,
            projectId: sessionData.projectId,
            screenshots: {},
            taskDetails: sessionData.taskDetails || '',
            taskHours: taskHours,
            taskID: sessionData.taskId || '',
            taskMinutes: taskMinutes,
            taskName: sessionData.taskName,
            timeZone: timeZone,
            userId: sessionData.userId || '',
            utc: endMoment.toDate().toUTCString(),
            source: 'web',
            sessionId: sessionData.sessionId || '',
            isSplit: false,
          },
        },
      ];

      console.log('🟢 splitSessionByDates: Single entry created', {
        entry: singleEntry[0],
        totalSeconds,
        taskHours,
        taskMinutes,
      });

      return singleEntry;
    }

    console.log(
      '🟢 splitSessionByDates: Multi-day session detected, starting split process'
    );

    // Get midnight boundaries
    const boundaries = getMidnightBoundaries(startTime, endTime, timeZone);
    const splitEntries = [];

    console.log('🟢 splitSessionByDates: Creating time segments', {
      boundariesCount: boundaries.length,
    });

    // Create time segments
    const timeSegments = [];
    let segmentStart = startMoment.clone();

    // Add segments for each day
    for (let i = 0; i < boundaries.length; i++) {
      const boundary = boundaries[i];
      const segmentEnd = moment(boundary).tz(timeZone);

      console.log(`🟢 splitSessionByDates: Creating segment ${i + 1}`, {
        segmentStart: segmentStart.toString(),
        segmentEnd: segmentEnd.toString(),
        segmentStartISO: segmentStart.toDate().toISOString(),
        segmentEndISO: segmentEnd.toDate().toISOString(),
      });

      timeSegments.push({
        start: segmentStart.clone(),
        end: segmentEnd.clone(),
      });
      segmentStart = segmentEnd.clone();
    }

    // Add final segment
    console.log('🟢 splitSessionByDates: Creating final segment', {
      segmentStart: segmentStart.toString(),
      segmentEnd: endMoment.toString(),
      segmentStartISO: segmentStart.toDate().toISOString(),
      segmentEndISO: endMoment.toDate().toISOString(),
    });

    timeSegments.push({
      start: segmentStart.clone(),
      end: endMoment.clone(),
    });

    console.log('🟢 splitSessionByDates: All time segments created', {
      totalSegments: timeSegments.length,
      segments: timeSegments.map((seg, idx) => ({
        index: idx,
        start: seg.start.toString(),
        end: seg.end.toString(),
        durationMs: seg.end.toDate().getTime() - seg.start.toDate().getTime(),
      })),
    });

    // Create timelog entries for each segment
    for (let i = 0; i < timeSegments.length; i++) {
      const segment = timeSegments[i];
      const segmentDuration = Math.floor(
        (segment.end.toDate() - segment.start.toDate()) / 1000
      );

      console.log(`🟢 splitSessionByDates: Processing segment ${i + 1}`, {
        segmentIndex: i,
        segmentDurationSeconds: segmentDuration,
        segmentStart: segment.start.toString(),
        segmentEnd: segment.end.toString(),
      });

      // Skip segments with no duration
      if (segmentDuration <= 0) {
        console.log(
          `🟢 splitSessionByDates: Skipping segment ${i + 1} - no duration`
        );
        continue;
      }

      const taskHours = Math.floor(segmentDuration / 3600);
      const taskMinutes = Math.floor((segmentDuration % 3600) / 60);

      // Use segment start time for date key (which day the work was performed) - FIXED
      const segmentDate = getDateKey(segment.start.toDate(), timeZone);
      // Keep using end time for timestamp key (when the segment was completed)
      const timestampKey = formatTimezoneString(segment.end.toDate(), timeZone);

      console.log(
        `🟢 splitSessionByDates: Creating entry for segment ${i + 1}`,
        {
          segmentIndex: i,
          segmentDate,
          timestampKey,
          taskHours,
          taskMinutes,
          segmentDurationSeconds: segmentDuration,
          usingStartTimeForDate: true,
        }
      );

      const entry = {
        date: segmentDate,
        timestampKey: timestampKey,
        entry: {
          companyID: sessionData.companyId || '',
          idleMinutes: null,
          projectId: sessionData.projectId,
          screenshots: {},
          taskDetails: sessionData.taskDetails || '',
          taskHours: taskHours,
          taskID: sessionData.taskId || '',
          taskMinutes: taskMinutes,
          taskName: sessionData.taskName,
          timeZone: timeZone,
          userId: sessionData.userId || '',
          utc: segment.end.toDate().toUTCString(),
          source: 'web',
          sessionId: sessionData.sessionId || '',
          isSplit: true,
          splitIndex: i + 1,
          totalSplits: timeSegments.length,
        },
      };

      splitEntries.push(entry);

      console.log(`🟢 splitSessionByDates: Entry ${i + 1} created and added`, {
        entryIndex: i + 1,
        date: entry.date,
        timestampKey: entry.timestampKey,
        taskHours: entry.entry.taskHours,
        taskMinutes: entry.entry.taskMinutes,
      });
    }

    console.log('🟢 splitSessionByDates: Split process completed', {
      totalSegments: timeSegments.length,
      totalEntries: splitEntries.length,
      splitEntries: splitEntries.map((entry, idx) => ({
        index: idx + 1,
        date: entry.date,
        taskHours: entry.entry.taskHours,
        taskMinutes: entry.entry.taskMinutes,
        isSplit: entry.entry.isSplit,
        splitIndex: entry.entry.splitIndex,
        totalSplits: entry.entry.totalSplits,
      })),
    });

    return splitEntries;
  } catch (error) {
    console.error('🟢 splitSessionByDates: Error splitting session by dates:', {
      error: error.message,
      stack: error.stack,
      startTime,
      endTime,
      timeZone,
    });
    // Return fallback single entry
    const totalSeconds = Math.floor(
      (new Date(endTime) - new Date(startTime)) / 1000
    );
    const taskHours = Math.floor(totalSeconds / 3600);
    const taskMinutes = Math.floor((totalSeconds % 3600) / 60);

    const fallbackEntry = [
      {
        date: getDateKey(endTime, timeZone),
        timestampKey: formatTimezoneString(endTime, timeZone),
        entry: {
          companyID: sessionData.companyId || '',
          idleMinutes: null,
          projectId: sessionData.projectId,
          screenshots: {},
          taskDetails: sessionData.taskDetails || '',
          taskHours: taskHours,
          taskID: sessionData.taskId || '',
          taskMinutes: taskMinutes,
          taskName: sessionData.taskName,
          timeZone: timeZone,
          userId: sessionData.userId || '',
          utc: new Date(endTime).toUTCString(),
          source: 'web',
          sessionId: sessionData.sessionId || '',
          isSplit: false,
        },
      },
    ];

    console.log(
      '🟢 splitSessionByDates: Returning fallback entry due to error',
      {
        fallbackEntry: fallbackEntry[0],
      }
    );

    return fallbackEntry;
  }
};

/**
 * Validates session duration
 * @param {Date|string} startTime - Session start time
 * @param {Date|string} endTime - Session end time
 * @returns {Object} Validation result with success flag and messages
 */
export const validateSessionDuration = (startTime, endTime) => {
  try {
    const startDate = new Date(startTime);
    const endDate = new Date(endTime);
    const durationSeconds = Math.floor((endDate - startDate) / 1000);
    const durationHours = durationSeconds / 3600;

    // Check minimum duration (1 minute)
    if (durationSeconds < 60) {
      return {
        isValid: false,
        message:
          'Timer session is less than 1 minute. Please continue tracking time or cancel the session.',
        durationSeconds,
      };
    }

    // Check maximum duration (24 hours)
    // if (durationHours > 24) {
    //   return {
    //     isValid: false,
    //     message: 'Timer session exceeds 24 hours maximum limit.',
    //     durationSeconds
    //   };
    // }

    return {
      isValid: true,
      message: 'Session duration is valid',
      durationSeconds,
    };
  } catch (error) {
    console.error('Error validating session duration:', error);
    return {
      isValid: false,
      message: 'Invalid session times',
      durationSeconds: 0,
    };
  }
};

/**
 * Generates unique session ID
 * @returns {string} Unique session identifier
 */
export const generateSessionId = () => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};
export const formatTimeFromSeconds = (seconds) => {
  const sec = Math.floor(Number(seconds) || 0);
  if (sec < 60) {
    return `${sec}s`;
  }
  const minutes = Math.floor(sec / 60);
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const secs = sec % 60;
  if (hours > 0) {
    return `${hours}h ${mins}m ${secs}s`;
  }
  return `${minutes}m ${secs}s`;
};

export const formatTimeFromFirestoreTimestamp = (timestamp) => {
  const now = new Date();
  const createdDate = new Date(timestamp.toDate());
  const diffInSeconds = Math.floor(
    (now.getTime() - createdDate.getTime()) / 1000
  );

  const minutes = Math.floor(diffInSeconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    return `${days}d ago`;
  } else if (hours > 0) {
    return `${hours}h ago`;
  } else if (minutes > 0) {
    return `${minutes}m ago`;
  } else {
    return 'Just now';
  }
};
